import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { FiClock, FiCheckCircle, FiAlertCircle, FiLoader } from "react-icons/fi";

const statusStyles = {
  pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
  in_progress: "bg-blue-100 text-blue-700 border-blue-300",
  completed: "bg-green-100 text-green-700 border-green-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

const statusIcons = {
  pending: <FiClock className="text-yellow-600" />,
  in_progress: <FiLoader className="animate-spin text-blue-600" />,
  completed: <FiCheckCircle className="text-green-600" />,
  cancelled: <FiAlertCircle className="text-red-600" />,
};

const ServiceStatus = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("service_requests")
          .select("*")
          .eq("user_id", user?.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setRequests(data || []);
      } catch (err) {
        console.error("Error fetching service requests:", err);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchRequests();
  }, [user]);

  return (
    <section className="py-16 bg-gray-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          className="text-3xl font-bold text-center text-gray-800 mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Your Service Requests
        </motion.h2>

        {loading ? (
          <div className="text-center text-gray-500">Loading service requests...</div>
        ) : requests.length === 0 ? (
          <div className="text-center text-gray-500">
            No service requests found.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {requests.map((req, index) => (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-md border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {req.service_type}
                  </h3>
                  <div
                    className={`flex items-center gap-2 text-sm px-3 py-1 rounded-full border ${statusStyles[req.status]}`}
                  >
                    {statusIcons[req.status]}
                    <span className="capitalize">{req.status.replace("_", " ")}</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-3">
                  {req.description || "No description provided."}
                </p>

                <p className="text-sm text-gray-400">
                  Requested on{" "}
                  {new Date(req.created_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceStatus;
